import { useNavigate } from "react-router-dom";
import type { ReactNode } from "react";
import { Button, HelpDialog, type HelpSection } from "@jkast90/kast-common/ui";
import styles from "./InstructionsDialog.module.css";

/** The text of each topic, by id. */
const CONTENT: Record<string, ReactNode> = {
  welcome: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>👋</span> Welcome to Kindly!
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>What is Kindly?</h3>
          <p>
            Kindly is your family's home for managing wishlists, celebrating
            memories, and staying connected through thoughtful gift-giving. It's
            designed to make holidays, birthdays, and special occasions easier
            and more meaningful.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>How It Works</h3>
          <p>
            Create wishlists so family members know what you want. Browse what
            others are wishing for and claim gifts you plan to give. Share
            memories and keep track of important family events on the calendar.
            It's all about bringing your family closer together!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Let's Take a Tour!</h3>
          <p>
            Click "Next" to explore each feature of Kindly. We'll walk through
            wishlists, browsing, memories, the calendar, thank you notes, and
            profiles. You can skip ahead at any time by clicking the tabs above.
          </p>
        </div>
      </div>
    </div>
  ),
  wishlists: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>🎁</span> Managing Your Wishlists
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>Add a Wishlist Item</h3>
          <p>
            Go to the Wishlist page and click the{" "}
            <Button size="sm" disabled>
              Add Item
            </Button>{" "}
            button. Fill in the description, add a link if available, set a
            priority, and specify size/quantity if needed. Your family members
            will be able to see and claim these items!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Edit Your Items</h3>
          <p>
            Click the{" "}
            <Button size="sm" disabled>
              ✏️
            </Button>{" "}
            button next to any of your wishlist items to update the details. You
            can change the description, link, price, priority, category, size,
            or quantity.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>View Family Wishlists</h3>
          <p>
            Use the dropdown at the top of the Wishlist page to switch between
            your wishlist and your family members' wishlists. This helps you see
            what everyone wants without spoiling your own surprises!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Delete Items</h3>
          <p>
            Click the{" "}
            <Button size="sm" disabled>
              🗑️
            </Button>{" "}
            button to remove items you no longer want. Only you can delete your
            own wishlist items.
          </p>
        </div>
      </div>
    </div>
  ),
  browse: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>🛍️</span> Claiming Gifts for Family
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>Browse Family Wishlists</h3>
          <p>
            The Browse page shows all your family members' wishlist items
            organized by person. This is where you claim gifts you plan to give!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Claim a Gift</h3>
          <p>
            Click the{" "}
            <Button size="sm" disabled>
              Claim
            </Button>{" "}
            button on any unclaimed item. You'll be asked to set a "reveal date"
            - this is when the recipient can see that you got them this gift
            (typically the birthday/holiday when you'll give it).
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Unclaim a Gift</h3>
          <p>
            Changed your mind? Click the{" "}
            <Button size="sm" disabled>
              Unclaim
            </Button>{" "}
            button on items you've claimed to release them back for others to
            claim.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Send Thank You Notes</h3>
          <p>
            After you've claimed a gift and the reveal date has passed, you can
            send a thank you note to acknowledge the gift. The{" "}
            <Button size="sm" disabled>
              💌 Thank You
            </Button>{" "}
            button will appear for claimed items.
          </p>
        </div>
      </div>
    </div>
  ),
  memories: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>💭</span> Capturing Family Memories
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>Create a Memory</h3>
          <p>
            Click the{" "}
            <Button size="sm" disabled>
              Create Memory
            </Button>{" "}
            button on the Memories page. Add a title, description, date, photos,
            and tag family members who were part of this special moment.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Memory Suggestions</h3>
          <p>
            Stuck on what to share? The Memories page shows suggested prompts
            like "What's your first memory?" or "When was the first time you met
            [Family Member]?" Click any suggestion to start a memory with that
            prompt.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Add Comments</h3>
          <p>
            On any memory, you can add comments to share your own perspective or
            additional details about that moment. It's a great way to build on
            each other's stories!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Tag Family Members</h3>
          <p>
            When creating or editing a memory, use the "Tag Family Members"
            section to select who was part of this memory. They'll be able to
            see and comment on it too!
          </p>
        </div>
      </div>
    </div>
  ),
  calendar: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>📅</span> Family Calendar & Events
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>View the Calendar</h3>
          <p>
            The Calendar page shows all family events including birthdays
            (automatically pulled from profiles), memories with dates, and
            custom family events you create.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Create an Event</h3>
          <p>
            Click{" "}
            <Button size="sm" disabled>
              Create Event
            </Button>{" "}
            to add family gatherings, anniversaries, graduations, or any special
            occasion. Set a date, location, and invite family members!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Invite Family</h3>
          <p>
            When creating an event, use the "Invite Family Members" dropdown to
            select who's coming. They'll see the event on their calendar too.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Link Memories</h3>
          <p>
            Events can be linked to memories! After a family gathering, create a
            memory and it will automatically appear on the calendar on that
            date.
          </p>
        </div>
      </div>
    </div>
  ),
  thankyou: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>💌</span> Expressing Gratitude
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>Send a Thank You Note</h3>
          <p>
            After receiving a gift, click{" "}
            <Button size="sm" disabled>
              💌 Thank You
            </Button>{" "}
            on the Browse page (for items that were claimed for you), or go to
            the Thank You page and create a new note.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>View Received Thanks</h3>
          <p>
            On the Thank You page, you can see all the thank you notes you've
            received from family members. It's heartwarming to see your
            thoughtfulness appreciated!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Track Sent Notes</h3>
          <p>
            The Thank You page also shows which thank you notes you've sent,
            helping you remember who you've thanked and for what.
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Feed Activity</h3>
          <p>
            Thank you notes appear in the family activity feed on the home page,
            so everyone can see the love and gratitude being shared!
          </p>
        </div>
      </div>
    </div>
  ),
  profiles: (
    <div className={styles.content}>
      <h2>
        <span className={styles.emoji}>👤</span> Managing Profiles
      </h2>
      <div className={styles.instructionGroup}>
        <div className={styles.instruction}>
          <h3>Your Profile</h3>
          <p>
            Update your profile with your birthday, clothing sizes, favorite
            color, and a bio. This helps family members choose the perfect gifts
            and remember important details about you!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Birthdays</h3>
          <p>
            When you add your birthday to your profile, it automatically appears
            on everyone's family calendar year after year. No more forgetting
            birthdays!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>Clothing Sizes</h3>
          <p>
            Add your shirt size, pants size, and other preferences to your
            profile. When family members are shopping for you, they can easily
            check your sizes without having to ask!
          </p>
        </div>
        <div className={styles.instruction}>
          <h3>View Family Profiles</h3>
          <p>
            Click on the Profiles page to see all your family members'
            information, birthdays, and preferences. It's your family directory
            all in one place.
          </p>
        </div>
      </div>
    </div>
  ),
};

/** A topic: its tab (an emoji and a name), the text for its id, and for a tour step the page it is about. */
function topic(
  id: string,
  label: string,
  emoji: string,
  route?: string,
): HelpSection {
  return { id, label, icon: emoji, route, content: CONTENT[id] };
}

const SECTIONS = [
  topic("wishlists", "Wishlists", "🎁"),
  topic("browse", "Browse", "🛍️"),
  topic("memories", "Memories", "💭"),
  topic("calendar", "Calendar", "📅"),
  topic("thankyou", "Thank You", "💌"),
  topic("profiles", "Profiles", "👤"),
];

// The tour goes through each feature, and the page it is about.
const TOUR = [
  topic("welcome", "Welcome", "👋"),
  topic("wishlists", "Wishlists", "🎁", "/wishlist"),
  topic("browse", "Browse", "🛍️", "/browse"),
  topic("memories", "Memories", "💭", "/memories"),
  topic("calendar", "Calendar", "📅", "/calendar"),
  topic("thankyou", "Thank You", "💌", "/feed"),
  topic("profiles", "Profiles", "👤", "/profile"),
];

export default function InstructionsDialog({
  open,
  onClose,
  tourMode = false,
  onStartTour,
}: {
  open: boolean;
  onClose: () => void;
  tourMode?: boolean;
  onStartTour?: () => void;
}) {
  const navigate = useNavigate();
  return (
    <HelpDialog
      open={open}
      onClose={onClose}
      title={
        <>
          <span className={styles.animatedEmoji}>🎁</span>
          Welcome to Kindly
          <span className={styles.animatedEmoji}>💝</span>
        </>
      }
      description="Your family's home for wishlists, memories, and staying connected"
      sections={SECTIONS}
      tour={TOUR}
      tourMode={tourMode}
      onStartTour={onStartTour}
      onNavigate={navigate}
      note={
        <>
          <strong>Remember:</strong> Kindly is all about bringing family
          together through thoughtful gift-giving and shared memories. If you
          have questions or need help, just ask! 💝
        </>
      }
      labels={{
        sections: "Help topics",
        skip: "Skip Tour",
        finish: "Get Started! 🚀",
        done: "Got it! Let's get started! 🎉",
        startTour: "Take the Tour",
      }}
    />
  );
}
